import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { countryOption } from "../components/CountryName.tsx";
import type { FilterOption } from "../components/filters/specs.ts";
import type { FilterState } from "../components/filters/useFilters.ts";
import { parseCommaSeparated } from "../utils/urlFilters.ts";
import { useCommaParam, type ParamUpdates, type UrlState } from "./useUrlState.ts";
import { useUiPreference } from "./useUiPreference.ts";

/** The `country` filter value for rows whose country is unknown. */
export const NO_COUNTRY = "none";
const NO_COUNTRIES: string[] = [];
const isCountryCode = (value: string) => value === NO_COUNTRY || /^[A-Z]{2}$/.test(value);
const isCountryCodes = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((v) => typeof v === "string" && isCountryCode(v));
const UNKNOWN_COUNTRY_OPTION: FilterOption = { value: NO_COUNTRY, label: "Unknown" };

/**
 * A `country` filter the reviewer's last choice stands in for: the URL's
 * `?country=` wins, else the countries saved under `preferenceKey` (shared by
 * the pages that use the same key, kept until changed). Returns the codes in
 * force, the pill options (the tenant's countries plus any remembered one it
 * has none from, and "Unknown"), and a `FilterState` for `useFilters` that
 * reads the remembered countries and saves a change.
 *
 * `onChange` lets a page add to a filter change (a signal page drops the
 * workflow when the data type changes).
 */
export function useRememberedCountryFilter({
  url,
  preferenceKey,
  queryKey,
  loadCountries,
  onChange,
}: {
  url: UrlState;
  preferenceKey: string;
  queryKey: readonly unknown[];
  loadCountries: () => Promise<string[]>;
  onChange?: (updates: ParamUpdates) => void;
}) {
  const { searchParams, changeFilter } = url;
  const countryParam = useCommaParam(searchParams, "country");
  const [savedCountries, setSavedCountries] = useUiPreference<string[]>(
    preferenceKey,
    NO_COUNTRIES,
    isCountryCodes,
  );
  const countryFilter = searchParams.has("country")
    ? countryParam.map((c) => (c === NO_COUNTRY ? c : c.toUpperCase())).filter(isCountryCode)
    : savedCountries;

  const { data: countries = NO_COUNTRIES, isLoading: loading } = useQuery({
    queryKey,
    queryFn: loadCountries,
    staleTime: 5 * 60_000,
  });
  // A remembered country this tenant has no rows from still shows by name.
  const extraCountries = countryFilter
    .filter((c) => c !== NO_COUNTRY && !countries.includes(c))
    .join(",");
  const countryOptions = useMemo(
    () =>
      [...countries, ...parseCommaSeparated(extraCountries)]
        .map((code) => ({ value: code, label: countryOption(code).label }))
        .concat(UNKNOWN_COUNTRY_OPTION),
    [countries, extraCountries],
  );

  // The bar reads the URL, with the remembered countries standing in for a
  // missing `country`; a country change is remembered for the next visit.
  const filterState = useMemo<FilterState>(() => {
    const params = new URLSearchParams(searchParams);
    if (!params.has("country") && savedCountries.length) {
      params.set("country", savedCountries.join(","));
    }
    return {
      searchParams: params,
      changeFilter: (updates, replace) => {
        const next = { ...updates };
        onChange?.(next);
        if ("country" in next) {
          const value = next.country;
          const codes = Array.isArray(value) ? value : parseCommaSeparated(value);
          setSavedCountries(codes.length ? codes : null);
          next.country = codes.length ? codes.join(",") : null;
        }
        changeFilter(next, replace);
      },
    };
  }, [searchParams, savedCountries, setSavedCountries, changeFilter, onChange]);

  return { countryFilter, countryOptions, loading, filterState };
}
